import { useState } from "react"
import { useParams, useNavigate, Link } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Input, Card, CardContent, CardHeader, CardTitle, Select, Skeleton } from "../components/ui"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Avatar } from "../components/avatar"
import { Trash2, UserPlus, Mail, Copy, Check, Link2, Zap, Lock } from "lucide-react"
import { LIMITS, getPlan } from "../lib/plans"

type Member = {
  id: string
  role: string
  user: { id: string; name: string | null; email: string; image: string | null }
}

type Invitation = { id: string; email: string; role: string; token: string; expiresAt: string }

/** Members management page. Lists workspace members with roles, lets you invite new ones by email, and shows pending invitations. */
export default function MembersPage() {
  const { workspaceId } = useParams({ from: "/workspaces/$workspaceId/members" })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: session } = authClient.useSession()

  const [email, setEmail] = useState("")
  const [role, setRole] = useState("member")
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null)

  const copyLink = (token: string, id: string) => {
    const url = `${window.location.origin}/invite/${token}`
    navigator.clipboard.writeText(url)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ id: string; name: string; role: string; plan: string; memberCount: number }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })

  const { data: members } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: () => fetcher<Member[]>(`/api/members?workspaceId=${workspaceId}`),
    enabled: !!session,
  })

  const { data: invitations } = useQuery({
    queryKey: ["invitations", workspaceId],
    queryFn: () => fetcher<Invitation[]>(`/api/members/invitations?workspaceId=${workspaceId}`),
    enabled: !!session && (workspace?.role === "owner" || workspace?.role === "admin"),
  })

  const invite = useMutation({
    mutationFn: (body: { workspaceId: string; email: string; role: string }) =>
      fetcher<{ invite?: { token: string }; inviteUrl?: string }>("/api/members", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["members", workspaceId] })
      queryClient.invalidateQueries({ queryKey: ["invitations", workspaceId] })
      if (data?.invite?.token) {
        setLastInviteUrl(`${window.location.origin}/invite/${data.invite.token}`)
        toast("Invite link created. Copy it below", "success")
      } else {
        toast("Member added directly", "success")
      }
      setEmail("")
      setRole("member")
    },
    onError: (err: any) => toast(err.message || "Failed to invite", "error"),
  })

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      fetcher(`/api/members/${id}`, { method: "PATCH", body: JSON.stringify({ role }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members", workspaceId] })
      toast("Role updated", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to update role", "error"),
  })

  const removeMember = useMutation({
    mutationFn: (id: string) => fetcher(`/api/members/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members", workspaceId] })
      toast("Member removed", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to remove member", "error"),
  })

  const cancelInvite = useMutation({
    mutationFn: (id: string) => fetcher(`/api/members/invitations/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", workspaceId] })
      toast("Invitation canceled", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to cancel invitation", "error"),
  })

  if (!session) {
    navigate({ to: "/login" })
    return null
  }
  if (!workspace) return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-64 space-y-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )

  const isOwner = workspace.role === "owner"
  const isAdmin = isOwner || workspace.role === "admin"
  const memberLimit = LIMITS[getPlan(workspace.plan)].members
  const memberLimitLabel = memberLimit === Infinity ? "Unlimited" : memberLimit
  const atMemberLimit = memberLimit !== Infinity && (workspace.memberCount ?? 0) >= memberLimit

  return (
    <Shell
      title={workspace.name}
      back={{ to: "/workspaces/$workspaceId", label: "Workspace" }}
      tabs={
        <div className="flex gap-4 text-sm">
          <Link to="/workspaces/$workspaceId" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
            Projects
          </Link>
          <span className="border-b-2 border-primary pb-2 font-medium">Members</span>
          <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
            Billing
          </Link>
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/audit" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
              Audit log
            </Link>
          )}
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/webhooks" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
              Webhooks
            </Link>
          )}
        </div>
      }
    >
      <main className="mx-auto w-full max-w-5xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight">Members</h2>
          <span className="text-sm text-muted-foreground">
            {workspace.memberCount ?? members?.length ?? 0} / {memberLimitLabel} members
          </span>
        </div>

        {atMemberLimit && isAdmin && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-medium text-foreground">Member limit reached</p>
                  <p className="text-sm text-muted-foreground">Your plan allows {memberLimitLabel} members. Upgrade to add more.</p>
                </div>
              </div>
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
                <Button size="sm"><Zap className="mr-1 h-4 w-4" /> Upgrade</Button>
              </Link>
            </div>
          </div>
        )}

        {isAdmin && !atMemberLimit && (
          <Card className="mb-6 overflow-hidden rounded-xl border-border bg-card shadow-sm">
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
              <UserPlus className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-medium">Invite member</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => { e.preventDefault(); invite.mutate({ workspaceId, email, role }) }} className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required className="flex-1" />
                <Select value={role} onChange={(e) => setRole(e.target.value)} options={[{ value: "member", label: "Member" }, { value: "admin", label: "Admin" }]} className="w-32 shrink-0" />
                <Button type="submit" isLoading={invite.isPending} className="shrink-0">Invite</Button>
              </form>
              {lastInviteUrl && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
                  <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-xs text-muted-foreground">{lastInviteUrl}</span>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(lastInviteUrl); setCopiedId("last"); setTimeout(() => setCopiedId(null), 2000) }}
                    className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-muted"
                  >
                    {copiedId === "last" ? <><Check className="h-3 w-3 text-green-500" /> Copied</> : <><Copy className="h-3 w-3" /> Copy link</>}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden rounded-xl border-border bg-card shadow-sm">
          <CardContent className="p-0">
            {members?.map((m, idx) => (
              <div key={m.id} className={`flex items-center justify-between px-4 py-3 ${idx !== (members?.length ?? 0) - 1 ? "border-b border-border" : ""}`}>
                <div className="flex items-center gap-3">
                  <Avatar id={m.user.id} name={m.user.name} email={m.user.email} image={m.user.image} size="md" />
                  <div>
                    <p className="font-medium">{m.user.name || m.user.email}</p>
                    <p className="text-sm text-muted-foreground">{m.user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {session.user.id === m.user.id ? (
                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize text-muted-foreground">{m.role}</span>
                  ) : isOwner ? (
                    <Select value={m.role} onChange={(e) => updateRole.mutate({ id: m.id, role: e.target.value })} options={[{ value: "member", label: "Member" }, { value: "admin", label: "Admin" }, { value: "owner", label: "Owner" }]} disabled={updateRole.isPending} />
                  ) : isAdmin && m.role === "member" ? (
                    <Select value={m.role} onChange={(e) => updateRole.mutate({ id: m.id, role: e.target.value })} options={[{ value: "member", label: "Member" }, { value: "admin", label: "Admin" }]} disabled={updateRole.isPending} />
                  ) : (
                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize text-muted-foreground">{m.role}</span>
                  )}
                  {isAdmin && session.user.id !== m.user.id && (
                    <Button variant="ghost" size="sm" onClick={() => removeMember.mutate(m.id)} isLoading={removeMember.isPending && removeMember.variables === m.id}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>


        {invitations && invitations.length > 0 && (
          <>
            <h2 className="mb-4 mt-8 text-xl font-semibold tracking-tight">Pending invitations</h2>
            <Card className="overflow-hidden rounded-xl border-border bg-card shadow-sm">
              <CardContent className="p-0">
                {invitations.map((inv, idx) => (
                  <div key={inv.id} className={`flex items-center justify-between px-4 py-3 ${idx !== invitations.length - 1 ? "border-b border-border" : ""}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{inv.email}</p>
                        <p className="text-sm text-muted-foreground capitalize">{inv.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copyLink(inv.token, inv.id)}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="Copy invite link"
                      >
                        {copiedId === inv.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                        {copiedId === inv.id ? "Copied" : "Copy link"}
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => cancelInvite.mutate(inv.id)} isLoading={cancelInvite.isPending && cancelInvite.variables === inv.id}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </Shell>
  )
}
